'use client';

import { c as _c } from "react/compiler-runtime";
import { SegmentPowerBar } from '@/domains/pages/ai-proficiency/components/segment-power-bar';
import { ProficiencyTrendChip } from '@/domains/pages/ai-proficiency/components/proficiency-trend-chip';
import { segmentNounIcon } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { OrgScoreSnapshot } from '@/domains/pages/ai-proficiency/utils/map-aggregates';
import type { SegmentSummary } from '@/domains/pages/ai-proficiency/utils/map-segment-aggregates';
import type { SegmentScoreStats } from '@/domains/pages/ai-proficiency/utils/segment-comparisons';
import type { TimeRangeKind } from '@/api/generated/model';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
type SegmentScoreCardProps = {
  segmentName: string;
  segmentDisplayName: string;
  segmentScore: OrgScoreSnapshot;
  segmentRank: number;
  segmentCount: number;
  segmentNoun: string;
  segmentNounPlural: string;
  scoreStats: SegmentScoreStats;
  peers: SegmentSummary[];
  timeRangeKind: TimeRangeKind;
};
export function SegmentScoreCard(t0) {
  const $ = _c(37);
  const {
    segmentName,
    segmentDisplayName,
    segmentScore,
    segmentRank,
    segmentCount,
    segmentNoun,
    segmentNounPlural,
    scoreStats,
    peers,
    timeRangeKind
  } = t0;
  const delta = segmentScore.delta;
  let t1;
  if ($[0] !== segmentNoun) {
    t1 = segmentNounIcon(segmentNoun);
    $[0] = segmentNoun;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const SegmentIcon = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("shrink-0 text-[44px] leading-none font-medium tracking-tight", AIFI_DATA_NUMBER_CLASS);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== segmentScore.score) {
    t3 = formatScore(segmentScore.score, {
      decimals: 1
    });
    $[3] = segmentScore.score;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-2xl text-muted-foreground">/100</span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <span className={t2}>{t3}{t4}</span>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== delta || $[9] !== timeRangeKind) {
    t6 = delta != null ? <ProficiencyTrendChip delta={delta} timeRangeKind={timeRangeKind} /> : null;
    $[8] = delta;
    $[9] = timeRangeKind;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="flex w-full min-w-0 flex-wrap items-end gap-x-3 gap-y-2">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== SegmentIcon || $[15] !== segmentCount || $[16] !== segmentDisplayName || $[17] !== segmentNounPlural || $[18] !== segmentRank) {
    t8 = segmentRank > 0 && segmentCount > 0 ? <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2"><span className="flex min-w-0 items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium text-foreground"><SegmentIcon className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={2.25} aria-hidden={true} /><span className="line-clamp-1">{segmentDisplayName}</span></span><span className={cn("text-sm text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}>#{segmentRank} of {segmentCount} {segmentNounPlural}</span></div> : null;
    $[14] = SegmentIcon;
    $[15] = segmentCount;
    $[16] = segmentDisplayName;
    $[17] = segmentNounPlural;
    $[18] = segmentRank;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== segmentDisplayName || $[21] !== segmentNounPlural) {
    t9 = <p className="mt-2 text-sm leading-snug text-muted-foreground">{segmentDisplayName} against the organization and its other {segmentNounPlural}, scored 0 to 100.</p>;
    $[20] = segmentDisplayName;
    $[21] = segmentNounPlural;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== peers || $[24] !== scoreStats.bestName || $[25] !== scoreStats.bestScore || $[26] !== scoreStats.median || $[27] !== segmentDisplayName || $[28] !== segmentName || $[29] !== segmentNoun || $[30] !== segmentScore.score) {
    t10 = <SegmentPowerBar className="mt-6" selectedName={segmentName} selectedDisplayName={segmentDisplayName} selectedScore={segmentScore.score} median={scoreStats.median} bestScore={scoreStats.bestScore} bestName={scoreStats.bestName} peers={peers} segmentNoun={segmentNoun} />;
    $[23] = peers;
    $[24] = scoreStats.bestName;
    $[25] = scoreStats.bestScore;
    $[26] = scoreStats.median;
    $[27] = segmentDisplayName;
    $[28] = segmentName;
    $[29] = segmentNoun;
    $[30] = segmentScore.score;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t11 = <div data-testid="segment-score-card">{t7}{t8}{t9}{t10}</div>;
    $[32] = t10;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}